/* eslint-disable react-refresh/only-export-components -- Provider + consumer hook co-located intentionally */
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useTutorialEnabled } from '@/hooks/useTutorialEnabled';

import {
  getStep,
  getSteps,
  getTutorialSteps,
  getWelcomeTutorialSteps,
  loadStepRegistry,
} from './steps';
import { TutorialController } from './TutorialController';
import { TutorialOutro } from './TutorialOutro';
import type {
  TutorialContextValue,
  TutorialStep,
  TutorialTrigger,
} from './types';

const TutorialContext = createContext<TutorialContextValue | null>(null);

const NOOP_CONTEXT: TutorialContextValue = {
  isActive: false,
  currentStep: 0,
  totalSteps: 0,
  triggeredBy: 'user',
  marcusMessage: undefined,
  startTutorial: () => {},
  dismissTutorial: () => {},
  resumeTutorial: () => {},
  next: () => {},
  prev: () => {},
  skip: () => {},
  goToStep: () => {},
};

export function TutorialProvider({ children }: { children: ReactNode }) {
  const enabled = useTutorialEnabled();
  const [registryReady, setRegistryReady] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [showOutro, setShowOutro] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<TutorialStep[]>([]);
  const [triggeredBy, setTriggeredBy] = useState<TutorialTrigger>('user');
  const [marcusMessage, setMarcusMessage] = useState<string | undefined>();

  useEffect(() => {
    if (!enabled) return;
    loadStepRegistry().then(() => setRegistryReady(true));
  }, [enabled]);

  const totalSteps = steps.length;

  function completeMarcusTutorial() {
    window.dispatchEvent(
      new CustomEvent('tutorial-completed', {
        detail: { triggeredBy: 'marcus' },
      })
    );
    setTriggeredBy('user');
    setMarcusMessage(undefined);
  }

  function startTutorial(customSteps?: TutorialStep[]) {
    const s = customSteps ?? getWelcomeTutorialSteps();
    if (s.length === 0) return;
    setSteps(s);
    setCurrentStep(0);
    setIsActive(true);
    setTriggeredBy('user');
    setMarcusMessage(undefined);
  }

  function dismissTutorial() {
    if (triggeredBy === 'marcus') {
      completeMarcusTutorial();
    }
    setIsActive(false);
    setCurrentStep(0);
  }

  function resumeTutorial(fromStep: number) {
    if (fromStep >= 0 && fromStep < steps.length) {
      setCurrentStep(fromStep);
      setIsActive(true);
    }
  }

  function next() {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(p => p + 1);
    } else {
      const lastStep = steps[steps.length - 1];
      if (triggeredBy === 'marcus') {
        completeMarcusTutorial();
      } else if (lastStep?.id === 'tour-complete') {
        setShowOutro(true);
      }
      setIsActive(false);
      setCurrentStep(0);
    }
  }

  function prev() {
    if (currentStep > 0) {
      setCurrentStep(p => p - 1);
    }
  }

  function goToStep(step: number) {
    if (step >= 0 && step < totalSteps) {
      setCurrentStep(step);
    }
  }

  const queuedCommandRef = useRef<CustomEvent | null>(null);

  type TutorialDetail = {
    action?: string;
    stepId?: string;
    stepIds?: string[];
    tutorialId?: string;
    message?: string;
  };

  function processTutorialCommand(detail: TutorialDetail) {
    if (!detail?.action) return;

    switch (detail.action) {
      case 'start':
        startTutorial();
        break;
      case 'tutorial': {
        if (detail.tutorialId) {
          const resolved = getTutorialSteps(detail.tutorialId);
          if (resolved.length > 0) {
            startTutorial(resolved);
          }
        }
        break;
      }
      case 'step': {
        const step = detail.stepId ? getStep(detail.stepId) : undefined;
        if (step) {
          startTutorial([step]);
        }
        break;
      }
      case 'steps': {
        if (detail.stepIds?.length) {
          const resolved = getSteps(detail.stepIds);
          if (resolved.length > 0) {
            startTutorial(resolved);
          }
        }
        break;
      }
      case 'resume':
        resumeTutorial(currentStep);
        break;
    }

    setTriggeredBy('marcus');
    setMarcusMessage(detail.message || undefined);
  }

  useEffect(() => {
    function handleTutorialCommand(e: Event) {
      const detail = (e as CustomEvent).detail as TutorialDetail | undefined;
      if (!detail?.action) return;

      if (!registryReady) {
        queuedCommandRef.current = e as CustomEvent;
        return;
      }
      processTutorialCommand(detail);
    }
    window.addEventListener('tutorial-command', handleTutorialCommand);
    return () =>
      window.removeEventListener('tutorial-command', handleTutorialCommand);
  });

  useEffect(() => {
    if (!registryReady || !queuedCommandRef.current) return;
    const detail = queuedCommandRef.current.detail as TutorialDetail;
    queuedCommandRef.current = null;
    processTutorialCommand(detail);
  }, [registryReady]);

  const value: TutorialContextValue = {
    isActive,
    currentStep,
    totalSteps,
    triggeredBy,
    marcusMessage,
    startTutorial,
    dismissTutorial,
    resumeTutorial,
    next,
    prev,
    skip: dismissTutorial,
    goToStep,
  };

  const lastStep = steps[steps.length - 1];

  if (!enabled) {
    return <TutorialContext value={NOOP_CONTEXT}>{children}</TutorialContext>;
  }

  return (
    <TutorialContext value={value}>
      {children}
      {isActive && steps.length > 0 && (
        <TutorialController
          steps={steps}
          currentStep={currentStep}
          onNext={next}
          onPrev={prev}
          onSkip={dismissTutorial}
          marcusMessage={marcusMessage}
        />
      )}
      {showOutro && lastStep && (
        <TutorialOutro
          onComplete={() => setShowOutro(false)}
          dialogTitle={lastStep.title}
          dialogBody={lastStep.body}
          stepLabel={`${steps.length} / ${steps.length}`}
        />
      )}
    </TutorialContext>
  );
}

export function useTutorial(): TutorialContextValue {
  return useContext(TutorialContext) ?? NOOP_CONTEXT;
}
